import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    CircularProgress
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import DataFileService from '../../../services/DataFileService';
import TipsContext from '../../../providers/GlobalTip';

function DateEditModal({ open, onClose, dataFile, clientId, onUpdate }) {
    const [selectedDate, setSelectedDate] = useState(null);
    const [saving, setSaving] = useState(false);
    const tip_context = TipsContext.useMessage();

    useEffect(() => {
        if (dataFile && open) {
            // Initialize with current DataThroughDate
            setSelectedDate(moment(dataFile.DataThroughDate).toDate());
        }
    }, [dataFile, open]);

    const handleSave = async () => {
        if (!selectedDate || !dataFile) return;

        setSaving(true);
        try {
            const formattedDate = moment(selectedDate).format('YYYY-MM-DD HH:mm:ss');
            const currentTimestamp = moment().format('YYYY-MM-DD HH:mm:ss');

            const result = await DataFileService.updateDataFile(clientId, dataFile.ClientDataFileId, {
                DataThroughDate: formattedDate,
                ProcessedDate: currentTimestamp
            });

            tip_context.showSuccess("Data through date updated successfully");
            onUpdate(result);
            onClose();
        } catch (error) {
            console.error('Error updating data through date:', error);
            tip_context.show("Failed to update data through date");
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (!saving) {
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Edit Data Through Date
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        <strong>File:</strong> {dataFile?.Name}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                        Period: {dataFile ? moment(dataFile.Period, 'YYYYMM').format('MMM YYYY') : ''}
                    </Typography>

                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DesktopDatePicker
                            label="Data Through Date"
                            value={selectedDate}
                            onChange={(newValue) => setSelectedDate(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    variant="outlined"
                                    disabled={saving}
                                />
                            )}
                            disabled={saving}
                        />
                    </LocalizationProvider>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving || !selectedDate}
                    startIcon={saving ? <CircularProgress size={20} /> : null}
                >
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default DateEditModal;
